import type { ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { CIRCULARITY_ADVISORY_FOOTER, CIRCULARITY_ADVISORY_HEADER } from './circularityCopy';

/**
 * SIL-473 — the visual-quarantine shell EVERY module on the Circularity tab
 * lives inside (per this ticket: "everything on this tab lives inside a
 * visually-quarantined advisory panel"). Mirrors the SIL-425/SIL-440
 * `CarbonAdvisoryModule` prototype's four quarantine devices (sky/cyan
 * color family reserved for advisory content, a striped "do not cross"
 * header band, the §10 header baked into the panel, the §10 footer scoped
 * to the whole panel) rather than importing that component directly — its
 * props are typed to the Financials scenario-forecast domain
 * (`CarbonAdvisoryPanel`/`AdvisoryCohortEntry`), which doesn't fit this
 * tab's Sankey/comparison/goal-bar shape. This shell is the reusable,
 * domain-agnostic version of the same pattern; `children` supplies the
 * content, this file supplies the quarantine.
 */
export function AdvisoryQuarantinePanel({ children }: { children: ReactNode }) {
  return (
    <section
      data-testid="circularity-advisory-panel"
      className="relative overflow-hidden rounded-xl border border-sky-300 bg-sky-50 p-5 pt-8"
    >
      {/* Striped "do not cross" header band — decorative only; the
       * disclaimer itself is the text below, so it survives a cropped
       * screenshot. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-6"
        style={{
          background: 'repeating-linear-gradient(-45deg, rgba(14,165,233,.20) 0 8px, transparent 8px 16px)',
        }}
      />
      <div className="relative space-y-5">
        <div className="flex items-start gap-1.5 text-xs font-bold uppercase tracking-wider text-sky-800">
          <ShieldAlert aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          <span>{CIRCULARITY_ADVISORY_HEADER}</span>
        </div>

        {children}

        <p className="border-t border-sky-200 pt-3 text-xs leading-relaxed text-sky-900/80">
          {CIRCULARITY_ADVISORY_FOOTER}
        </p>
      </div>
    </section>
  );
}
